/**
 * Route-to-breadcrumb trail mapping configuration.
 *
 * Each entry maps a URL pattern to an ordered array of breadcrumb crumbs.
 * Dynamic crumbs (company name, meter label) are resolved at runtime
 * by the `useBreadcrumbs` hook from `location.state` or Redux.
 *
 * Patterns are matched top-down — first match wins. More specific
 * patterns must appear before general ones.
 */

/** A single breadcrumb crumb in the trail definition. */
export interface BreadcrumbCrumbConfig {
  /** i18n key for the crumb label, or `'$companyName'` / `'$meterLabel'` for dynamic resolution */
  labelKey: string
  /** Static path, or a function receiving route params to build the path */
  path: string | ((params: Record<string, string>) => string)
  /** Whether this crumb uses a dynamic label resolved from state/Redux */
  isDynamic?: boolean
}

/** A route pattern mapped to its breadcrumb trail. */
export interface BreadcrumbRouteConfig {
  /** Regex to match against `location.pathname` */
  pattern: RegExp
  /** Named capture groups from the pattern (for param extraction) */
  paramNames: string[]
  /** Ordered breadcrumb crumbs from root to current page */
  crumbs: BreadcrumbCrumbConfig[]
}

/**
 * Extracts named params from a pathname using a route config's pattern.
 */
export const extractParams = (pathname: string, config: BreadcrumbRouteConfig): Record<string, string> => {
  const match = pathname.match(config.pattern)
  if (!match) return {}
  const params: Record<string, string> = {}
  config.paramNames.forEach((name, index) => {
    params[name] = match[index + 1] || ''
  })
  return params
}

/**
 * Breadcrumb route configurations.
 *
 * Order matters: more specific patterns first.
 * Only deep pages (3+ segments under `/companies/:id/*`) are configured.
 */
export const breadcrumbRoutes: BreadcrumbRouteConfig[] = [
  // /companies/:id/operationallog/new (depth 4)
  {
    pattern: /^\/companies\/([^/]+)\/operationallog\/new$/,
    paramNames: ['companyId'],
    crumbs: [
      { labelKey: 'navigation.companies', path: '/companies' },
      { labelKey: '$companyName', path: (p) => `/companies/${p.companyId}`, isDynamic: true },
      { labelKey: 'navigation.operationalLog', path: (p) => `/companies/${p.companyId}/operationallog` },
      { labelKey: 'company.newOperationalLog', path: '' },
    ],
  },
  // /companies/:id/meters/:meterid (depth 4)
  {
    pattern: /^\/companies\/([^/]+)\/meters\/([^/]+)$/,
    paramNames: ['companyId', 'meterId'],
    crumbs: [
      { labelKey: 'navigation.companies', path: '/companies' },
      { labelKey: '$companyName', path: (p) => `/companies/${p.companyId}`, isDynamic: true },
      { labelKey: 'navigation.meters', path: (p) => `/companies/${p.companyId}/meters` },
      { labelKey: '$meterLabel', path: '', isDynamic: true },
    ],
  },
  // /companies/:id/history (depth 3)
  {
    pattern: /^\/companies\/([^/]+)\/history$/,
    paramNames: ['companyId'],
    crumbs: [
      { labelKey: 'navigation.companies', path: '/companies' },
      { labelKey: '$companyName', path: (p) => `/companies/${p.companyId}`, isDynamic: true },
      { labelKey: 'company.maintenanceHistory', path: '' },
    ],
  },
  // /companies/:id/operationallog (depth 3)
  {
    pattern: /^\/companies\/([^/]+)\/operationallog$/,
    paramNames: ['companyId'],
    crumbs: [
      { labelKey: 'navigation.companies', path: '/companies' },
      { labelKey: '$companyName', path: (p) => `/companies/${p.companyId}`, isDynamic: true },
      { labelKey: 'navigation.operationalLog', path: '' },
    ],
  },
  // /companies/:id/personnelmanagement (depth 3)
  {
    pattern: /^\/companies\/([^/]+)\/personnelmanagement$/,
    paramNames: ['companyId'],
    crumbs: [
      { labelKey: 'navigation.companies', path: '/companies' },
      { labelKey: '$companyName', path: (p) => `/companies/${p.companyId}`, isDynamic: true },
      { labelKey: 'navigation.personnel', path: '' },
    ],
  },
  // /companies/:id/technical (depth 3)
  {
    pattern: /^\/companies\/([^/]+)\/technical$/,
    paramNames: ['companyId'],
    crumbs: [
      { labelKey: 'navigation.companies', path: '/companies' },
      { labelKey: '$companyName', path: (p) => `/companies/${p.companyId}`, isDynamic: true },
      { labelKey: 'companyTechnical.tabLabel', path: '' },
    ],
  },
  // /companies/:id/meters (depth 3)
  {
    pattern: /^\/companies\/([^/]+)\/meters$/,
    paramNames: ['companyId'],
    crumbs: [
      { labelKey: 'navigation.companies', path: '/companies' },
      { labelKey: '$companyName', path: (p) => `/companies/${p.companyId}`, isDynamic: true },
      { labelKey: 'navigation.meters', path: '' },
    ],
  },
]
